import type { GroupLeader, GroupLeaderNode, RecordRef } from "../../../services/ActionReports/ActionReportAPI.types";

/**
 * Per-report record shape accepted by the helpers below. Each report uses a
 * different casing (`Aff`/`Abo` on WhosNew, `aff`/`abo` elsewhere), so the
 * callers supply an `affAbo` extractor returning the canonical lowercase
 * `{aff, abo}` tuple plus the record's ancestor ref.
 */
export interface HasFirstGroupLeader {
  firstGroupLeader?: RecordRef;
}

export interface LeaderSection<Record> {
  leader: GroupLeader | null; // null = `__other__` section
  records: Record[];
  children: LeaderSection<Record>[];
  count: number;
  pinLevel: number;
}

// Aff padding mismatch: LOS Lite returns each record's `aff` as the int's
// string form ("70"), but the GL compute pads leader/ancestor refs to 3
// digits ("070"). Normalize on every comparison so a record-vs-leader match
// works regardless of which side did the padding.
const padAff = (aff: string): string => {
  if (aff.length >= 3) return aff;
  return /^\d+$/.test(aff) ? aff.padStart(3, "0") : aff;
};

const keyFor = (ref: RecordRef): string => `${padAff(ref.aff)}|${ref.abo}`;

const sameRef = (a: RecordRef, b: RecordRef): boolean => padAff(a.aff) === padAff(b.aff) && a.abo === b.abo;

/**
 * Canonical `aff-abo` key used by the GroupLeaderFilter dropdown to compare
 * a leader option against the "leaders that actually have records on the
 * current view" set. Padding is normalized so producer-side keys derived
 * from records' `firstGroupLeader` match consumer-side keys derived from
 * the leader tree.
 */
export const leaderOptionKey = (ref: RecordRef): string => `${padAff(ref.aff)}-${ref.abo}`;

/**
 * Build the `activeLeaderKeys` set passed to GroupLeaderFilter. The set is
 * keyed by every record's `firstGroupLeader` so leaders with no records on
 * the current view (and orphans whose ancestor isn't a known leader) are
 * hidden from the dropdown.
 *
 * Note: `getAffAbo` is unused under the new backend contract (leaders'
 * `firstGroupLeader` points at their parent, not themselves) but is kept
 * in the signature for source compatibility with existing callers.
 */
export const buildActiveLeaderKeys = <R>(
  records: Iterable<R>,
  getAffAbo: (record: R) => RecordRef,
  getAncestor: (record: R) => RecordRef | undefined,
): Set<string> => {
  const keys = new Set<string>();
  for (const record of records) {
    const ref = getAncestor(record);
    if (!ref?.aff || !ref?.abo) continue;
    // Legacy backend shape: a leader's own record points at itself. Skip
    // those so a leader with no actual downline isn't surfaced as a
    // selectable option (single-self-row dropdown entry).
    if (sameRef(getAffAbo(record), ref)) continue;
    keys.add(leaderOptionKey(ref));
  }
  return keys;
};

/**
 * Flat grouping: one section per qualifying leader. Each record is assigned
 * to its `firstGroupLeader`'s section (no roll-up into ancestor leaders).
 * Records with no qualifying ancestor — or one that isn't present in the
 * leaders list — fall into a trailing `__other__` section.
 *
 * The backend now sets each leader's own `firstGroupLeader` to the parent
 * leader they sit under (or zero for top-level), so a leader's own row
 * naturally renders inside the parent's section without any FE-side
 * self-row filtering.
 */
export const buildLeaderSections = <R>(
  records: R[],
  tree: GroupLeaderNode[],
  getAncestor: (record: R) => RecordRef | undefined,
  getAffAbo: (record: R) => RecordRef,
): LeaderSection<R>[] => {
  // Bucket records by their firstGroupLeader key. Empty/zero refs are orphans.
  const byLeaderKey = new Map<string, R[]>();
  const orphans: R[] = [];

  // Pre-walk the tree to find each leader's parent leader (if any) so
  // self-referencing leader rows can be re-bucketed under their parent.
  const parentByLeaderKey = new Map<string, RecordRef>();
  const collectParents = (nodes: GroupLeaderNode[], parent: RecordRef | null): void => {
    for (const node of nodes) {
      const k = keyFor({ aff: node.aff, abo: node.abo });
      if (parent) parentByLeaderKey.set(k, parent);
      if (node.children?.length) collectParents(node.children, { aff: node.aff, abo: node.abo });
    }
  };
  collectParents(tree, null);

  for (const record of records) {
    const ref = getAncestor(record);
    if (!ref || (!ref.aff && !ref.abo)) {
      orphans.push(record);
      continue;
    }
    const self = getAffAbo(record);
    if (sameRef(self, ref)) {
      // Self-referencing firstGroupLeader. Only re-bucket if the record
      // is actually a known leader in the tree — a known top-level
      // leader is represented by their own section header so we drop
      // their self-row; a known nested leader's self-row gets routed
      // into their parent's section. A self-reference from a record
      // that ISN'T in the leader tree must fall through to orphans
      // rather than vanish silently.
      const selfKey = keyFor(self);
      const isKnownLeader = parentByLeaderKey.has(selfKey) || leaderExistsInTree(tree, self);
      if (isKnownLeader) {
        const parent = parentByLeaderKey.get(selfKey);
        if (!parent) continue;
        const parentKey = keyFor(parent);
        const parentBucket = byLeaderKey.get(parentKey);
        if (parentBucket) parentBucket.push(record);
        else byLeaderKey.set(parentKey, [record]);
        continue;
      }
      orphans.push(record);
      continue;
    }
    const key = keyFor(ref);
    const bucket = byLeaderKey.get(key);
    if (bucket) bucket.push(record);
    else byLeaderKey.set(key, [record]);
  }

  // Flatten the tree into a unique leader list, preserving the tree's order
  // so siblings line up with the dropdown. The previous nested-section
  // model meant a parent leader's count rolled up its descendants without
  // rendering their rows under the parent — flat assignment fixes that.
  const flatLeaders: GroupLeaderNode[] = [];
  const seenLeaderKeys = new Set<string>();
  const walk = (nodes: GroupLeaderNode[]): void => {
    for (const node of nodes) {
      const key = keyFor({ aff: node.aff, abo: node.abo });
      if (!seenLeaderKeys.has(key)) {
        seenLeaderKeys.add(key);
        flatLeaders.push(node);
      }
      if (node.children?.length) walk(node.children);
    }
  };
  walk(tree);

  const sections: LeaderSection<R>[] = [];
  for (const node of flatLeaders) {
    const key = keyFor({ aff: node.aff, abo: node.abo });
    const own = byLeaderKey.get(key) ?? [];
    // Count equals what recordsInLeaderSubtree would return if this
    // leader were selected — direct-bucket size, since records with
    // ancestor=this-leader and child-leader self-rows (re-bucketed under
    // their parent above) all land in this same bucket. Avoids an
    // O(L × R) re-walk per leader.
    const scopedCount = own.length;
    sections.push({
      leader: {
        aff: node.aff,
        abo: node.abo,
        name: node.name,
        entryDate: node.entryDate,
        pinLevel: node.pinLevel,
      },
      records: own,
      children: [],
      count: scopedCount,
      pinLevel: node.pinLevel,
    });
  }

  // Any record whose firstGroupLeader doesn't match any leader we know about
  // joins the orphan bucket so it still renders somewhere.
  for (const [key, bucket] of byLeaderKey) {
    if (!seenLeaderKeys.has(key)) orphans.push(...bucket);
  }

  if (orphans.length > 0) {
    sections.push({
      leader: null,
      records: orphans,
      children: [],
      count: orphans.length,
      pinLevel: 0,
    });
  }

  return sections;
};

/**
 * Return the records that bucket directly under the selected leader. Used
 * for the scoped view — selecting a leader from the dropdown shows only the
 * records whose `firstGroupLeader` is that leader, not records belonging to
 * sub-leaders nested below them. (Sub-leaders' records stay in their own
 * sections in the "All" view.) The leader's own self record is excluded
 * from the row list.
 */
export const recordsInLeaderSubtree = <R>(
  records: R[],
  tree: GroupLeaderNode[],
  selected: RecordRef,
  getAffAbo: (record: R) => RecordRef,
  getAncestor: (record: R) => RecordRef | undefined,
): R[] => {
  if (!leaderExistsInTree(tree, selected)) return [];

  const selectedKey = keyFor(selected);

  // Direct child-leaders of `selected` belong in the scoped section as
  // their own rows. Under the legacy backend shape their record's
  // firstGroupLeader points at themselves (not at `selected`), so the
  // ancestor match below would miss them.
  const childLeaderKeys = directChildLeaderKeys(tree, selected);

  return records.filter((record) => {
    const self = getAffAbo(record);
    const selfKey = keyFor(self);

    // Never include the selected leader's own row in the scoped view —
    // it's represented by the section's header.
    if (sameRef(self, selected)) return false;
    if (childLeaderKeys.has(selfKey)) return true;

    const ancestor = getAncestor(record);
    if (!ancestor) return false;
    return keyFor(ancestor) === selectedKey;
  });
};

/**
 * Find the parent leader of `child` in the tree. Returns null when `child`
 * is a top-level leader (no parent leader — they sit directly under the
 * login user / root) or is absent from the tree.
 */
export const parentLeaderOf = (tree: GroupLeaderNode[], child: RecordRef): RecordRef | null => {
  for (const node of tree) {
    if (node.children?.length) {
      for (const c of node.children) {
        if (sameRef({ aff: c.aff, abo: c.abo }, child)) {
          return { aff: node.aff, abo: node.abo };
        }
      }
      const deeper = parentLeaderOf(node.children, child);
      if (deeper) return deeper;
    }
  }
  return null;
};

const directChildLeaderKeys = (tree: GroupLeaderNode[], parent: RecordRef): Set<string> => {
  const keys = new Set<string>();
  const visit = (nodes: GroupLeaderNode[]): boolean => {
    for (const node of nodes) {
      if (sameRef({ aff: node.aff, abo: node.abo }, parent)) {
        for (const c of node.children ?? []) keys.add(keyFor({ aff: c.aff, abo: c.abo }));
        return true;
      }
      if (node.children?.length && visit(node.children)) return true;
    }
    return false;
  };
  visit(tree);
  return keys;
};

export const leaderExistsInTree = (nodes: GroupLeaderNode[], target: RecordRef): boolean => {
  for (const node of nodes) {
    if (sameRef({ aff: node.aff, abo: node.abo }, target)) return true;
    if (node.children?.length && leaderExistsInTree(node.children, target)) return true;
  }
  return false;
};
